/* Blackwater Soap Guild — About page: the shelf rail drift.

   Scope: this file does ONE thing. The handoff's prototype also carried a scroll
   reveal engine; in the theme the reveals are Dawn's own `.scroll-trigger
   animate--slide-in` (animations.js + base.css), which already honours the
   owner's "Reveal sections on scroll" setting and prefers-reduced-motion. Adding
   a second engine would animate the same nodes twice.

   Why setInterval and not requestAnimationFrame: per the handoff, embedded
   preview environments (the theme editor's iframe, some in-app browsers) do not
   reliably tick animation frames, and a rail that silently stops looks broken.
   A timer keeps running.

   Why a float accumulator instead of reading rail.scrollLeft each tick: at 1x
   DPR the browser snaps scrollLeft to whole pixels, so writing pos + 0.55 and
   reading it back gives 1 — the rail would drift at roughly double the intended
   ~18px/s. The authoritative position lives in `pos`; scrollLeft is only ever
   written from it, and re-read when the shopper takes over (manual scroll,
   arrow nudge, resume after a pause).

   Desktop only (owner, V1.30): below the theme's 768px breakpoint the rail never
   drifts — it opens on the first card and the arrows or a swipe move it. The
   loop and the clone cards stay, so the arrows still wrap 4 → 1 on a phone. The
   breakpoint is watched live, so a window dragged across it starts or stops the
   drift without a reload.

   Markup contract (sections/candells-about-shelf.liquid):
     [data-candells-section="about-shelf"]   section root, scopes the buttons
       [data-candells-shelf][data-drift]     the scroll container
         > first element child               the track (two identical card sets)
       [data-shelf-prev] / [data-shelf-next] arrow buttons
   The second card set is a clone, so one loop width is the offset of the first
   clone card from the first real card, and subtracting it makes the wrap
   invisible. */
(function () {
  'use strict';

  var TICK_MS = 30;
  var STEP_PX = 0.55; /* 0.55px every 30ms is about 18px/s. */
  var FALLBACK_CARD_STEP = 280;
  var DESKTOP_QUERY = '(min-width: 768px)'; /* the candells layer's single breakpoint */

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function setUpRail(rail) {
    var track = rail.firstElementChild;
    if (!track) return;

    /* Scope the arrow buttons to this section so two shelves on one page cannot
       drive each other. closest() is guarded because the rail may be orphaned
       in odd editor states. */
    var section = rail.closest ? rail.closest('[data-candells-section="about-shelf"]') : null;
    var scope = section || document;
    var prev = scope.querySelector('[data-shelf-prev]');
    var next = scope.querySelector('[data-shelf-next]');

    var paused = false;
    var pos = rail.scrollLeft;
    var resumeTimer = null;
    var TOUCH_SETTLE_MS = 1200; /* let a swipe's momentum finish before drifting again */

    /* The Liquid marks every clone card aria-hidden, so the real set is the run
       of cards without it. One loop = the distance from the first real card to
       the first clone. Measured from the cards rather than as half the track
       width: the track has one gap fewer than it has cards, so the halfway
       point sits half a gap short of the clone and the wrap would visibly hop. */
    var realCount = rail.querySelectorAll('.candells-about__card:not([aria-hidden])').length;

    function loopWidth() {
      var cards = rail.querySelectorAll('.candells-about__card');
      if (realCount > 0 && cards.length > realCount) {
        var distance = cards[realCount].offsetLeft - cards[0].offsetLeft;
        if (distance > 0) return distance;
      }
      return track.scrollWidth / 2;
    }

    /* A seamless wrap needs the rail to scroll at least one full loop. On a
       wide screen one card set can be narrower than the rail itself, so more
       clone sets are appended until it can (capped, in case the rail cannot be
       measured). Re-checked on resize; each appended set adds exactly one loop. */
    function fillForWrap() {
      var loop = loopWidth();
      if (!loop || !realCount) return;
      var guard = 0;
      while (rail.scrollWidth - rail.clientWidth < loop && guard < 6) {
        var cards = rail.querySelectorAll('.candells-about__card');
        for (var i = 0; i < realCount && i < cards.length; i++) {
          var clone = cards[i].cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          track.appendChild(clone);
        }
        guard++;
      }
    }
    fillForWrap();
    window.addEventListener('resize', fillForWrap);

    /* Measured on demand: card width is fluid (clamp), so caching it would go
       stale on every resize. */
    function cardStep() {
      var cards = rail.querySelectorAll('.candells-about__card');
      if (cards.length > 1) {
        var step = cards[1].offsetLeft - cards[0].offsetLeft;
        if (step > 0) return step;
      }
      return FALLBACK_CARD_STEP;
    }

    function pause() {
      if (resumeTimer) {
        window.clearTimeout(resumeTimer);
        resumeTimer = null;
      }
      paused = true;
    }

    function resume() {
      resumeTimer = null;
      paused = false;
      pos = rail.scrollLeft;
    }

    /* A finger lifting off is not the end of the scroll: momentum keeps the rail
       moving, and a programmatic scrollLeft write would cancel it. Wait for the
       swipe to settle before the drift takes over again. */
    function resumeAfterTouch() {
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(resume, TOUCH_SETTLE_MS);
    }

    rail.addEventListener('mouseenter', pause);
    rail.addEventListener('mouseleave', resume);
    rail.addEventListener('focusin', pause);
    rail.addEventListener('focusout', resume);
    rail.addEventListener('touchstart', pause, { passive: true });
    rail.addEventListener('touchend', resumeAfterTouch, { passive: true });
    rail.addEventListener('touchcancel', resumeAfterTouch, { passive: true });

    /* While the shopper is scrolling by hand the accumulator has to follow, or
       the next tick would yank the rail back to where the drift left off. Our
       own writes land within a pixel of `pos`; anything further away is the
       shopper (trackpad, momentum, scrollbar) and wins. */
    rail.addEventListener(
      'scroll',
      function () {
        if (paused || Math.abs(rail.scrollLeft - pos) > 1.5) pos = rail.scrollLeft;
      },
      { passive: true }
    );

    function nudge(dir) {
      var loop = loopWidth();
      var target = rail.scrollLeft + dir * cardStep();
      if (loop) {
        if (target < 0) target += loop;
        if (target >= loop) target -= loop;
      }
      rail.scrollLeft = target;
      pos = target;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        nudge(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        nudge(1);
      });
    }

    /* Everything above is manual control and always available. Only the
       automatic drift is optional, and it runs on desktop widths alone. */
    var desktop = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;

    function driftAllowed() {
      if (prefersReducedMotion()) return false;
      if (rail.getAttribute('data-drift') === 'false') return false;
      if (desktop && !desktop.matches) return false;
      return true;
    }

    function tick() {
      /* A section re-render in the theme editor replaces the node without
         firing shopify:section:unload; stop rather than tick a detached rail. */
      if (document.body && !document.body.contains(rail)) {
        detach();
        return;
      }
      if (paused) return;

      var loop = loopWidth();
      /* If the rail still cannot scroll a full loop (fill capped, or nothing to
         scroll at all) it stays put rather than stalling at the edge and
         snapping back. The arrows and swiping keep working. */
      if (!loop || rail.scrollWidth - rail.clientWidth < loop) return;

      pos += STEP_PX;
      if (pos >= loop) pos -= loop;
      rail.scrollLeft = pos;
    }

    function startDrift() {
      if (rail._candellsDrift || !driftAllowed()) return;
      pos = rail.scrollLeft;
      rail._candellsDrift = window.setInterval(tick, TICK_MS);
    }

    function stopDrift() {
      if (!rail._candellsDrift) return;
      window.clearInterval(rail._candellsDrift);
      rail._candellsDrift = null;
    }

    function onBreakpoint() {
      if (driftAllowed()) startDrift();
      else stopDrift();
    }

    /* Full teardown for a rail that has left the document or whose section is
       being unloaded by the theme editor. */
    function detach() {
      stopDrift();
      window.removeEventListener('resize', fillForWrap);
      if (desktop) {
        if (desktop.removeEventListener) desktop.removeEventListener('change', onBreakpoint);
        else if (desktop.removeListener) desktop.removeListener(onBreakpoint);
      }
    }
    rail._candellsDetach = detach;

    if (desktop) {
      if (desktop.addEventListener) desktop.addEventListener('change', onBreakpoint);
      else if (desktop.addListener) desktop.addListener(onBreakpoint);
    }

    startDrift();
  }

  function init(root) {
    if (!root || !root.querySelectorAll) return;
    var rails = root.querySelectorAll('[data-candells-shelf]');
    var i;
    for (i = 0; i < rails.length; i++) {
      if (rails[i].getAttribute('data-candells-shelf-ready')) continue;
      rails[i].setAttribute('data-candells-shelf-ready', 'true');
      setUpRail(rails[i]);
    }
  }

  function teardown(root) {
    if (!root || !root.querySelectorAll) return;
    var rails = root.querySelectorAll('[data-candells-shelf]');
    var i;
    for (i = 0; i < rails.length; i++) {
      if (typeof rails[i]._candellsDetach === 'function') {
        rails[i]._candellsDetach();
        rails[i]._candellsDetach = null;
      }
    }
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardown(event.target);
  });

  /* The tag is deferred, so the document is already parsed. */
  init(document);
})();
